import { Component, Input, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { EmojiEvent } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { filter, map, tap } from 'rxjs/operators';

import { AuthService } from '../../../../auth/auth.service';
import { ChatContext } from '../../../../coaching-base/chat-context.model';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../../common/take-until-destroy.decorator';
import { useDispatcher, UseStore } from '../../../../store.decorator';
import {
    ChatMenuDialogService,
    ChatMessage,
    HubMethods,
    SocketService,
} from '../../../../teams-call';
import { ChatNav } from '../../../../teams-call/chat-menu/dialog/model/chat-nav.model';
import { navigate } from '../../../../teams-call/chat-menu/dialog/state/navigate.action';
import { ChatMessageNotification } from '../../../../teams-call/core/model/chat-message-notification.model';
import { ChatNotification } from '../../../../teams-call/core/model/chat-notification.model';
import { copyTextToClipboard } from '../../../transcript-dialog/function/copy-text-to-clipboard.function';
import { ChatBubble } from '../model/chat-bubble.model';
import { ChatDialogData } from '../model/chat-dialog-data.model';
import { deleteMessages } from '../state/delete-messages.action';
import { load } from '../state/load.action';
import { receiveMessages } from '../state/receive-messages.action';
import { reload } from '../state/reload.action';
import { sendMessage } from '../state/send-message.action';
import { updateChat } from '../state/update-chat.action';
import { TeamschatDialogComponent } from '../teamschat-dialog.component';
import { teamschatDialogReducer } from '../teamschat-dialog.reducer';
import { TeamschatDialogState } from '../teamschat-dialog.state';
import { ChatTranscriptDialogData } from '../teamschat-transcript-dialog/model/chat-transcript-dialog-data.model';
import { TeamschatTranscriptsDialogBoxComponent } from '../teamschat-transcript-dialog/teamschat-transcript-dialog.component';

import { TranslationKeyMap } from './chat-box.i18n';

@Component({
    selector: 'cmps-chat-box',
    templateUrl: './chat-box.component.html',
    host: {
        class: 'cmps-chat-box',
    },
})
@TakeUntilDestroy()
@UseStore('coachingDetail.ChatBox', teamschatDialogReducer)
export class ChatBoxComponent implements OnInit {
    state: TeamschatDialogState;
    @Input() data: ChatDialogData;

    /** @private */
    _inputMessage: string;
    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _isEmojiPickerVisible: boolean;
    /** @private */
    _emojiPickerHasBeenOpen: boolean;

    constructor(
        private readonly _dialogRef: CmpsDialogRef<any, undefined>,
        private readonly _dialog: CmpsDialog,
        private readonly _socketService: SocketService,
        private readonly _dialogService: ChatMenuDialogService,
        private readonly _authService: AuthService,
    ) {
        this._inputMessage = '';
        this._emojiPickerHasBeenOpen = false;
    }

    ngOnInit(): void {
        this._socketService.message
            .pipe(
                filter((msg) => msg.hubMethod === HubMethods.OnChat),
                map((msg) => msg.payload),
                tap((payload: ChatNotification) => {
                    useDispatcher(
                        this,
                        reload({
                            chat: payload.chat ?? {},
                            userId: this._authService.user.id,
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._socketService.message
            .pipe(
                filter(
                    (msg) =>
                        msg.hubMethod === HubMethods.OnChatMessage ||
                        msg.hubMethod === HubMethods.OnChatMessageList,
                ),
                map((msg) => msg.payload),
                tap((payload: ChatMessageNotification) => {
                    useDispatcher(
                        this,
                        receiveMessages({ messages: payload.messages ?? [] }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onHasValidTokenChange(hasValidToken: boolean): void {
        if (!hasValidToken) {
            console.log('no valid msft token');
            return;
        }

        /* TODO FIXME 'data' will be null when closing the chat-pane is closed while on 'chat-box', then reopened */
        if (this.data == null) {
            return;
        }
        useDispatcher(
            this,
            load({
                opts: this.data.chatRequest,
            }),
        );
    }

    /** @private */
    _onSubmit(form: NgForm): void {
        const value = form.value.message as string;
        if (value == null || value.trim().length === 0) {
            return;
        }

        const message: ChatMessage = {
            message: value,
        };

        useDispatcher(this, sendMessage({ message }));

        this._inputMessage = '';
    }

    /** @private */
    _onAddNotesClick(): void {
        let ctx: ChatContext | null;
        try {
            ctx = JSON.parse(this.data.chatRequest.context);
        } catch {}

        const data: ChatTranscriptDialogData = {
            chat: this.state.chat ?? {},
            context: ctx,
            messages: this.state.conversation,
            willNavigate: () => {
                this._dialogRef.close();
            },
        };

        this._dialog.open(TeamschatTranscriptsDialogBoxComponent, {
            resizable: true,
            disableBackdrop: true,
            disableClose: true,
            movable: true,
            data,
        });
        this._dialogRef.close();
    }

    /** @private */
    _onTopicChange(value: string): void {
        if (this.state?.chat?.topic === value) {
            return;
        }
        const chat = {
            ...this.state.chat,
            topic: value,
        };
        useDispatcher(
            this,
            updateChat({
                chat,
            }),
        );
    }

    /** @private */
    _onDeleteClick(deletedBubble: ChatBubble): void {
        useDispatcher(this, deleteMessages({ messages: [deletedBubble] }));
    }

    /** @private */
    _onCopyClick(message: string): void {
        copyTextToClipboard(message);
    }

    /** @private */
    _onEmojiSelect(event: EmojiEvent): void {
        this._inputMessage = `${this._inputMessage}${
            (event.emoji.native as string) ?? ''
        }`;
        this._isEmojiPickerVisible = false;
        this._emojiPickerHasBeenOpen = false;
    }

    /** @private */
    _onShiftEnterClick(): void {
        this._inputMessage = `${this._inputMessage}\n`;
    }

    /** @private */
    _onInputChange(e): void {
        e.target.style.height = '0px';
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        e.target.style.height = e.target.scrollHeight + 25 + 'px';
    }

    /** @private */
    _onReturnClick(): void {
        /* use of ChatMenuDialogService here is the exception */
        useDispatcher(this._dialogService, navigate({ nav: ChatNav.MENU }));
    }

    /** @private */
    _onExpandClick(): void {
        this._dialog.open(TeamschatDialogComponent, {
            resizable: true,
            disableClose: true,
            movable: true,
            data: this.data,
        });
        this._dialogRef.close();
    }

    /** @private */
    _onOutsideClick(): void {
        if (this._emojiPickerHasBeenOpen) {
            this._isEmojiPickerVisible = !this._isEmojiPickerVisible;
        }
        this._emojiPickerHasBeenOpen = !this._emojiPickerHasBeenOpen;
    }
}
